import type { CatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
} from "@/shared/lib/componentHierarchy";

export type CatalogPageState = {
  readonly isDesktop: boolean;
  readonly isThreadCreateOpen?: boolean;
  readonly isFetching?: boolean;
  readonly hasNextPage?: boolean;
  readonly isSettingsOpen: boolean;
  readonly isSortOpen: boolean;
  readonly isTagFilterOpen: boolean;
};

export type CatalogPageEvent = Extract<
  CatalogEvent,
  {
    type:
      | "catalog/sort-selected"
      | "catalog/page-selected"
      | "catalog/auto-reload-changed"
      | "catalog/refresh-requested"
      | "catalog/retry-requested"
      | "catalog/load-more-requested"
      | "catalog/sidebar-toggle-requested"
      | "catalog/scroll-requested"
      | "catalog/settings-link-clicked"
      | "catalog/settings-close-requested"
      | "catalog/sort-modal-open-requested"
      | "catalog/sort-modal-close-requested"
      | "catalog/tag-modal-open-requested"
      | "catalog/tag-modal-close-requested"
      | "catalog/list-view-requested"
      | "catalog/thread-create-open-requested"
      | "catalog/thread-create-close-requested"
      | "catalog/thread-create-toggle-requested"
      | "catalog/page-model-synchronized"
      | "catalog/viewport-synchronized";
  }
>;

export type CatalogPageEffect = Exclude<
  CatalogPageEvent,
  {
    type:
      | "catalog/page-model-synchronized"
      | "catalog/viewport-synchronized"
      | "catalog/settings-link-clicked"
      | "catalog/settings-close-requested"
      | "catalog/sort-modal-open-requested"
      | "catalog/sort-modal-close-requested"
      | "catalog/tag-modal-open-requested"
      | "catalog/tag-modal-close-requested"
      | "catalog/thread-create-toggle-requested";
  }
>;

export function isCatalogPageEvent(
  event: CatalogEvent,
): event is CatalogPageEvent {
  switch (event.type) {
    case "catalog/sort-selected":
    case "catalog/page-selected":
    case "catalog/auto-reload-changed":
    case "catalog/refresh-requested":
    case "catalog/retry-requested":
    case "catalog/load-more-requested":
    case "catalog/sidebar-toggle-requested":
    case "catalog/scroll-requested":
    case "catalog/settings-link-clicked":
    case "catalog/settings-close-requested":
    case "catalog/sort-modal-open-requested":
    case "catalog/sort-modal-close-requested":
    case "catalog/tag-modal-open-requested":
    case "catalog/tag-modal-close-requested":
    case "catalog/list-view-requested":
    case "catalog/thread-create-open-requested":
    case "catalog/thread-create-close-requested":
    case "catalog/thread-create-toggle-requested":
    case "catalog/page-model-synchronized":
    case "catalog/viewport-synchronized":
      return true;
    default:
      return false;
  }
}

export function createCatalogPageMediator(
  initialModel:
    | boolean
    | {
        readonly isDesktop: boolean;
        readonly isThreadCreateOpen?: boolean;
        readonly isFetching?: boolean;
        readonly hasNextPage?: boolean;
      },
  runEffect: (effect: CatalogPageEffect) => void,
  restoredState?: CatalogPageState,
): Mediator<CatalogPageState, CatalogPageEvent, CatalogPageEffect> {
  const model =
    typeof initialModel === "boolean"
      ? { isDesktop: initialModel }
      : initialModel;
  return createMediator<CatalogPageState, CatalogPageEvent, CatalogPageEffect>(
    {
      isDesktop: model.isDesktop,
      isThreadCreateOpen: model.isThreadCreateOpen ?? false,
      isFetching: model.isFetching ?? false,
      hasNextPage: model.hasNextPage ?? false,
      isSettingsOpen: restoredState?.isSettingsOpen ?? false,
      isSortOpen: restoredState?.isSortOpen ?? false,
      isTagFilterOpen: restoredState?.isTagFilterOpen ?? false,
    },
    transitionCatalogPage,
    { runEffect },
  );
}

export function transitionCatalogPage(
  state: CatalogPageState,
  event: CatalogPageEvent,
): MediatorTransitionResult<CatalogPageState, CatalogPageEffect> {
  switch (event.type) {
    case "catalog/page-model-synchronized":
      return {
        status: "handled",
        state: {
          isDesktop: event.isDesktop,
          isThreadCreateOpen: event.isThreadCreateOpen,
          isFetching: event.isFetching,
          hasNextPage: event.hasNextPage,
          isSettingsOpen: event.isDesktop ? state.isSettingsOpen : false,
          isSortOpen: event.isDesktop ? false : state.isSortOpen,
          isTagFilterOpen: event.isDesktop ? false : state.isTagFilterOpen,
        },
      };
    case "catalog/viewport-synchronized":
      return {
        status: "handled",
        state: {
          ...state,
          isDesktop: event.isDesktop,
          isSettingsOpen: event.isDesktop ? state.isSettingsOpen : false,
          isSortOpen: event.isDesktop ? false : state.isSortOpen,
          isTagFilterOpen: event.isDesktop ? false : state.isTagFilterOpen,
        },
      };
    case "catalog/thread-create-toggle-requested":
      return {
        status: "handled",
        state: { ...state, isThreadCreateOpen: !state.isThreadCreateOpen },
        effects: [
          {
            type: state.isThreadCreateOpen
              ? "catalog/thread-create-close-requested"
              : "catalog/thread-create-open-requested",
          },
        ],
      };
    case "catalog/thread-create-open-requested":
    case "catalog/thread-create-close-requested":
      return {
        status: "handled",
        state: {
          ...state,
          isThreadCreateOpen:
            event.type === "catalog/thread-create-open-requested",
        },
        effects: [event],
      };
    case "catalog/settings-link-clicked":
      if (event.hasModifier) return { status: "unhandled" };
      return {
        status: "handled",
        state: { ...state, isSettingsOpen: true },
      };
    case "catalog/settings-close-requested":
      return {
        status: "handled",
        state: { ...state, isSettingsOpen: false },
      };
    case "catalog/sort-modal-open-requested":
      return { status: "handled", state: { ...state, isSortOpen: true } };
    case "catalog/sort-modal-close-requested":
      return { status: "handled", state: { ...state, isSortOpen: false } };
    case "catalog/tag-modal-open-requested":
      return {
        status: "handled",
        state: { ...state, isTagFilterOpen: true },
      };
    case "catalog/tag-modal-close-requested":
      return {
        status: "handled",
        state: { ...state, isTagFilterOpen: false },
      };
    case "catalog/load-more-requested":
      return state.isFetching || !state.hasNextPage
        ? { status: "handled", state }
        : { status: "handled", state, effects: [event] };
    case "catalog/refresh-requested":
      return state.isFetching && !event.manual
        ? { status: "handled", state }
        : { status: "handled", state, effects: [event] };
    default:
      return { status: "handled", state, effects: [event] };
  }
}
